/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import {
    ISelection,
    Selection,
    SelectionMode,
    DetailsList,
    DetailsListLayoutMode,
    SearchBox,
    CheckboxVisibility
} from "@fluentui/react";
import * as React from "react";
import { GridCommands, IGridProps, IGridState } from "./GridModels";
import { GridCommandBar } from "./GridCommandBar";
import styles from "./Grid.module.css";

const gridCommands = new GridCommands();
const cmds = gridCommands.Cmds;

const Grid = (props: IGridProps): JSX.Element => {
    const initialState: IGridState = {
        selectedRows: [],
        filteredItems: props.items
    };

    const [state, dispatch] = React.useReducer(gridCommands.Reducer, initialState);

    React.useEffect(() => {
        dispatch(
            cmds.itemsChange({ items: props.items, columns: props.filterColumns, id: props.id })
        );
    }, [props.items]);

    const onFilter = (
        _: React.ChangeEvent<HTMLInputElement> | undefined,
        newValue: string | undefined
    ) => {
        dispatch(
            cmds.onFilter({
                searchString: newValue ?? "",
                allItems: props.items,
                filterColumns: props.filterColumns
            })
        );
    };

    // if selection is none, still use single behind the scenes so we can track row click, but hide checkbox
    const selectionMode = props.selectionMode || SelectionMode.single;
    const checkboxVisibility =
        props.selectionMode === SelectionMode.none
            ? CheckboxVisibility.hidden
            : CheckboxVisibility.onHover;

    const selection: ISelection = new Selection({
        selectionMode,
        onSelectionChanged: () => {
            const selectedRows = selection.getSelectedIndices();

            if (props.onSelectRow) {
                props.onSelectRow(selectedRows);
            }

            dispatch(cmds.onSelectionChanged(selectedRows));
        }
    });

    return (
        <div className={props.className}>
            {props.hasFilter && (
                <SearchBox className={styles.searchBox} placeholder="Filter" onChange={onFilter} />
            )}

            {props.actions && <GridCommandBar {...props.actions}></GridCommandBar>}

            <div className={styles.table}>
                <DetailsList
                    columns={props.columns}
                    items={state.filteredItems ?? []}
                    setKey={props.id}
                    getKey={item => item.name}
                    layoutMode={props.layout ?? DetailsListLayoutMode.justified}
                    selectionMode={selectionMode}
                    selection={selection}
                    selectionPreservedOnEmptyClick={true}
                    ariaLabelForSelectionColumn="Toggle item selection"
                    checkButtonAriaLabel="Select item"
                    checkboxVisibility={checkboxVisibility}
                />
            </div>
        </div>
    );
};

export default Grid;
